/**
 * Шейдеры для AdvisorObject
 * Вынесены в отдельный файл для лучшей читаемости и возможности подсветки синтаксиса GLSL
 */

/**
 * Vertex shader для AdvisorObject
 */
export const advisorVertexShader = /* glsl */`
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vPosition = position;
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Fragment shader для AdvisorObject
 */
export const advisorFragmentShader = /* glsl */`
  #define TAU 6.28318530718

  uniform float time;
  uniform vec2 resolution;
  uniform float radius;
  uniform vec3 baseColor;

  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vNormal;
  varying vec3 vPosition;

  float rand(vec2 n) { 
    return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
  }

  float noise(vec2 p) {
    vec2 ip = floor(p);
    vec2 u = fract(p);
    u = u*u*(3.0-2.0*u);
    float res = mix(
      mix(rand(ip),rand(ip+vec2(1.0,0.0)),u.x),
      mix(rand(ip+vec2(0.0,1.0)),rand(ip+vec2(1.0,1.0)),u.x),u.y);
    return res*res;	
  }

  float fbm(vec2 p, int octaves) {
    float s = 0.0;
    float m = 0.0;
    float a = 0.5;
    
    if (octaves >= 1)
    {
      s += a * noise(p);
      m += a;
      a *= 0.5;
      p *= 2.0;
    }
    
    if (octaves >= 2)
    {
      s += a * noise(p);
      m += a;
      a *= 0.5;
      p *= 2.0;
    }
    
    if (octaves >= 3)
    {
      s += a * noise(p);
      m += a;
      a *= 0.5;
      p *= 2.0;
    }
    
    if (octaves >= 4)
    {
      s += a * noise(p);
      m += a;
      a *= 0.5;
      p *= 2.0;
    }
    return s / m;
  }

  vec3 pal(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
    return a + b * cos(TAU * (c * t + d));
  }

  float luma(vec3 color) {
    return dot(color, vec3(0.299, 0.587, 0.114));
  }

  void main() {
    vec2 uv = vUv * 2.0 - 1.0;
    float t = time;
    float spin = t * 0.35;
    float wobble = sin(t * 0.9) * 0.08;
    mat2 rot = mat2(cos(spin), -sin(spin), sin(spin), cos(spin));
    uv = rot * (uv * (1.35 + wobble));

    float l = dot(uv, uv);
    vec3 col = vec3(0.0);
    
    float sm = smoothstep(2.5, 0.5, l);
    
    float d = sm * l * l * l * 2.0;
    vec3 norm = normalize(vec3(uv.x, uv.y, .7 - d));
    float nx = fbm(uv * 2.2 + t * 0.8 + 25.69, 4);
    float ny = fbm(uv * 2.2 + t * 0.7 + 86.31, 4);
    vec2 warp = vec2(nx, ny) * 0.8 + vec2(sin(t * 0.6), cos(t * 0.4)) * 0.25;
    float n = fbm(uv * 3.6 + 2.0 * warp, 3);
    col = vec3(n * 0.5 + 0.25);
    float a = atan(uv.y, uv.x) / TAU + t * 0.25 + n * 0.15;
    col *= pal(
      a,
      vec3(0.25, 0.25, 0.3),
      vec3(0.55, 0.5, 0.5),
      vec3(1.0, 0.9, 0.8),
      vec3(0.0, 0.8, 0.8)
    );
    col *= 2.4;
    vec3 cd = abs(col);
    vec3 c = col * d;
    c += (c * 0.5 + vec3(1.0) - luma(c)) * vec3(max(0.0, pow(dot(norm, vec3(0, 0, -1)), 5.0) * 3.0));
    float g = 1.7 * smoothstep(0.6, 1.0, fbm(norm.xy * 3.0 / (1.0 + norm.z) + t * 0.3, 2)) * d;
    c += g;
    col = c + col * pow((1.0 - smoothstep(1.0, 0.98, l) - pow(max(0.0, length(uv) - 1.0), 0.2)) * 2.0, 4.0);
    float f = fbm(normalize(uv) * 2. + t * 0.8, 2) + 0.1;
    vec2 uv2 = uv * (f + 0.1);
    uv2 *= 0.5;
    l = dot(uv2, uv2);
    vec3 ins = normalize(cd) + 0.1;
    float ind = 0.2 + pow(smoothstep(0.0, 1.5, sqrt(l)) * 48.0, 0.25);
    ind *= ind * ind * ind;
    ind = 1.0 / ind;
    ins *= ind;
    col += ins * ins * sm * smoothstep(0.7, 1.0, ind);
    col += abs(norm) * (1.0 - d) * sm * 0.25;
    
    float fresnel = 1.0 - abs(dot(normalize(vNormal), vec3(0.0, 0.0, 1.0)));
    fresnel = pow(fresnel, 2.0);
    
    vec2 fogUV = uv + vec2(sin(t * 0.6) * 0.15, cos(t * 0.55) * 0.15);
    float fogNoise = fbm(fogUV * 3.5 + t * 0.2, 3);
    float animatedFresnel = fresnel * (0.7 + fogNoise * 0.3);
    
    vec3 fogColor = pal(
      a + t * 0.2,
      vec3(0.2),
      vec3(0.35, 0.45, 0.6),
      vec3(1.0),
      vec3(0.1, 0.6, 0.8)
    );
    float fogIntensity = animatedFresnel * 2.0;
    col += fogColor * fogIntensity;
    
    float edgeFog = smoothstep(0.7, 1.0, l);
    float animatedEdgeFog = edgeFog * (0.75 + sin(t * 0.8 + a * 3.0) * 0.25);
    col += fogColor * animatedEdgeFog * 1.5;
    
    float distanceFog = smoothstep(1.5, 0.5, l);
    float animatedDistanceFog = distanceFog * (0.65 + cos(t * 0.7 + a * 2.0) * 0.35);
    col += fogColor * animatedDistanceFog * 1.0;
    
    col = mix(baseColor, col, 0.7);
    float pulse = 0.85 + sin(t * 1.4 + a * 6.0) * 0.15;
    float alpha = (0.18 + fresnel * 0.55 + edgeFog * 0.3) * pulse;
    alpha = min(alpha, 0.6);
    
    gl_FragColor = vec4(col, alpha);
  }
`
